import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { getElapsed } from '@sola/utils/relativeTime.ts';
import type { ContentFeedCardProps, ContentFeedMatch, ContentFeedMedia } from './content-feed-card.tsx';
const emptyMedia: ContentFeedMedia[] = [];

/**
 * Fallback dimensions for the Lightbox Image schema which requires numeric width/height.
 * These represent a standard 3:2 landscape ratio matching typical content feed imagery.
 * TODO: Remove once the upstream API guarantees dimensions on all media items.
 */
const FALLBACK_IMAGE_WIDTH = 700;
const FALLBACK_IMAGE_HEIGHT = 467;

/**
 * Derive banner text from the query match context.
 * Produces a relevance label based on the match tier and related entities.
 * When 1–2 participant names are available, displays them by name.
 */
const deriveBannerText = (match: ContentFeedMatch | undefined, t: ReturnType<typeof useTranslation>['t'], programCountryName?: string, participantNameMap?: Map<string, string>): string | undefined => {
  if (!match) {
    return t('my:contentFeed.bannerDefault');
  }
  const participantCount = match.related.participants?.length ?? 0;
  const firstParticipant = match.related.participants?.[0];
  switch (match.tier) {
    case 'participant':
      {
        if (firstParticipant?.localId && participantNameMap) {
          const name = participantNameMap.get(firstParticipant.localId);
          if (name) {
            return t('my:contentFeed.bannerNamed', {
              count: 1,
              name1: name,
              name2: ''
            });
          }
        }
        return t('my:contentFeed.bannerGeneric', {
          count: 1
        });
      }
    case 'fcp':
      {
        if (participantCount > 0 && participantCount <= 2 && participantNameMap) {
          const names = match.related.participants?.map(p => participantNameMap.get(p.localId)).filter(Boolean) as string[];
          if (names.length > 0 && names.length === participantCount) {
            return t('my:contentFeed.bannerNamed', {
              count: names.length,
              name1: names[0],
              name2: names[1] || ''
            });
          }
        }
        if (participantCount > 0) {
          return t('my:contentFeed.bannerGeneric', {
            count: participantCount
          });
        }
        return t('my:contentFeed.bannerDefault');
      }
    case 'cluster':
      {
        return t('my:contentFeed.bannerCluster');
      }
    case 'country':
      {
        if (programCountryName) {
          return t('my:contentFeed.bannerCountry', {
            country: programCountryName
          });
        }
        return t('my:contentFeed.bannerCountryGeneric');
      }
    default:
      {
        return t('my:contentFeed.bannerDefault');
      }
  }
};
export const useComponentContentFeedCard = props => {
  const $ = _c(53);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    sourceType,
    icon: t1,
    title,
    description,
    media: t2,
    fundingProgress,
    link,
    programCountryName,
    match,
    bannerText,
    hasNewUpdate: t3,
    ctaLabel,
    publishedAt,
    author
  } = props;
  const icon = t1 === undefined ? "heart-circle" : t1;
  const media = t2 === undefined ? emptyMedia : t2;
  const hasNewUpdate = t3 === undefined ? false : t3;
  const isCausesLayout = sourceType === "causes" || sourceType === "causes-progress-updates" || !sourceType;
  const isPostLayout = sourceType === "community" || sourceType === "compassion.media";
  let authorLine;
  if (author) {
    authorLine = author.role ? `${author.displayName}, ${author.role}` : author.displayName;
  }
  let t4;
  if ($[1] !== publishedAt) {
    t4 = publishedAt ? getElapsed(publishedAt) : undefined;
    $[1] = publishedAt;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const elapsed = t4;
  const formattedPublishedAt = elapsed && typeof elapsed === "string" ? elapsed : undefined;
  let t5;
  if ($[3] !== media) {
    const imageMedia = media.filter(_temp);
    const videoMedia = media.filter(_temp2);
    t5 = [...imageMedia, ...videoMedia];
    $[3] = media;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const allMedia = t5;
  let t6;
  if ($[5] !== allMedia) {
    t6 = allMedia.slice(0, 3);
    $[5] = allMedia;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const galleryItems = t6;
  const totalPhotos = allMedia.length;
  let t7;
  if ($[7] !== bannerText || $[8] !== match || $[9] !== programCountryName || $[10] !== props.participantNameMap || $[11] !== t) {
    t7 = bannerText ?? deriveBannerText(match, t, programCountryName, props.participantNameMap);
    $[7] = bannerText;
    $[8] = match;
    $[9] = programCountryName;
    $[10] = props.participantNameMap;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const resolvedBannerText = t7;
  const progressPercent = Math.min(fundingProgress?.percentageFunded ?? 0, 100);
  let t8;
  if ($[13] !== fundingProgress || $[14] !== t) {
    t8 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.raised
    }) : "";
    $[13] = fundingProgress;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const formattedRaised = t8;
  let t9;
  if ($[16] !== fundingProgress || $[17] !== t) {
    t9 = fundingProgress ? t("core:numbers.currencyCustom", {
      amount: fundingProgress.goal
    }) : "";
    $[16] = fundingProgress;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const formattedGoal = t9;
  const isFundedOrClosed = fundingProgress?.status === "funded" || fundingProgress?.status === "closed";
  let t10;
  if ($[19] !== fundingProgress || $[20] !== isFundedOrClosed || $[21] !== t) {
    t10 = isFundedOrClosed && fundingProgress?.fundedDate ? t("my:contentFeed.fundedOn", {
      date: fundingProgress.fundedDate
    }) : undefined;
    $[19] = fundingProgress;
    $[20] = isFundedOrClosed;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const fundedMessage = t10;
  let t11;
  if ($[23] !== allMedia || $[24] !== title) {
    t11 = allMedia.map(m_1 => {
      if (m_1.type === "video") {
        return {
          media: {
            video: {
              url: m_1.url,
              thumbnailUrl: m_1.thumbnailUrl,
              width: m_1.width,
              height: m_1.height
            },
            image: m_1.thumbnailUrl ? {
              url: m_1.thumbnailUrl,
              alt: m_1.alt || title,
              width: m_1.width ?? FALLBACK_IMAGE_WIDTH,
              height: m_1.height ?? FALLBACK_IMAGE_HEIGHT
            } : undefined
          }
        };
      }
      return {
        media: {
          image: {
            url: m_1.url,
            alt: m_1.alt || title,
            width: m_1.width ?? FALLBACK_IMAGE_WIDTH,
            height: m_1.height ?? FALLBACK_IMAGE_HEIGHT
          }
        }
      };
    });
    $[23] = allMedia;
    $[24] = title;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const lightboxItems = t11;
  let t12;
  if ($[26] !== ctaLabel || $[27] !== t) {
    t12 = ctaLabel ?? t("my:contentFeed.donate");
    $[26] = ctaLabel;
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const resolvedCtaLabel = t12;
  let t13;
  if ($[29] !== author || $[30] !== authorLine || $[31] !== description || $[32] !== formattedGoal || $[33] !== formattedPublishedAt || $[34] !== formattedRaised || $[35] !== fundedMessage || $[36] !== fundingProgress || $[37] !== galleryItems || $[38] !== hasNewUpdate || $[39] !== icon || $[40] !== isCausesLayout || $[41] !== isFundedOrClosed || $[42] !== isPostLayout || $[43] !== lightboxItems || $[44] !== link || $[45] !== programCountryName || $[46] !== progressPercent || $[47] !== resolvedBannerText || $[48] !== resolvedCtaLabel || $[49] !== t || $[50] !== title || $[51] !== totalPhotos) {
    t13 = {
      isCausesLayout,
      isPostLayout,
      author,
      authorLine,
      formattedPublishedAt,
      description,
      icon,
      title,
      fundingProgress,
      link,
      programCountryName,
      hasNewUpdate,
      galleryItems,
      totalPhotos,
      resolvedBannerText,
      progressPercent,
      formattedRaised,
      formattedGoal,
      fundedMessage,
      isFundedOrClosed,
      lightboxItems,
      resolvedCtaLabel,
      t
    };
    $[29] = author;
    $[30] = authorLine;
    $[31] = description;
    $[32] = formattedGoal;
    $[33] = formattedPublishedAt;
    $[34] = formattedRaised;
    $[35] = fundedMessage;
    $[36] = fundingProgress;
    $[37] = galleryItems;
    $[38] = hasNewUpdate;
    $[39] = icon;
    $[40] = isCausesLayout;
    $[41] = isFundedOrClosed;
    $[42] = isPostLayout;
    $[43] = lightboxItems;
    $[44] = link;
    $[45] = programCountryName;
    $[46] = progressPercent;
    $[47] = resolvedBannerText;
    $[48] = resolvedCtaLabel;
    $[49] = t;
    $[50] = title;
    $[51] = totalPhotos;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  return t13;
};
function _temp(m) {
  return m.type === "image";
}
function _temp2(m_0) {
  return m_0.type === "video";
}